import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Alert, Typography, CircularProgress, Box, Divider, Link,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';

import { parseArchivoSueldos, type ErrorImport } from '../../utils/importSueldos';
import { cuitDigits } from '../../utils/format';
import { MONTO_MAX_DEFAULT } from '../../utils/validations';
import { triggerBlobDownload } from '../../utils/reportes/download';

interface NominaRow {
  id: string;
  cuil: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Nómina precargada de la DDJJ (para matchear por CUIL). */
  nomina: NominaRow[];
  /** Aplica los sueldos importados (rowId -> sueldo como string). */
  onApply: (updates: Record<string, string>) => void;
}

interface Resumen {
  updates: Record<string, string>;
  actualizados: number;
  ignorados: string[];
  errores: ErrorImport[];
  archivo: string;
}

const ACCEPT = '.csv,.xlsx';

export default function ImportarSueldosDialog({ open, onClose, nomina, onApply }: Props) {
  const [parsing, setParsing] = useState(false);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setResumen(null);
    setError(null);
    setParsing(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-seleccionar el mismo archivo
    if (!file) return;
    setError(null);
    setResumen(null);
    setParsing(true);
    try {
      const { filas, errores } = await parseArchivoSueldos(file, { maxSueldo: MONTO_MAX_DEFAULT });

      // Matcheo por CUIL contra la nómina; sobrescribe los coincidentes,
      // ignora (e informa) los que no están en la nómina (#496).
      const cuilToRowId = new Map(nomina.map((n) => [cuitDigits(n.cuil), n.id]));
      const updates: Record<string, string> = {};
      const ignorados: string[] = [];
      for (const f of filas) {
        const rowId = cuilToRowId.get(f.cuilDigits);
        if (rowId) updates[rowId] = String(f.sueldo);
        else ignorados.push(f.cuilDigits);
      }

      setResumen({
        updates,
        actualizados: Object.keys(updates).length,
        ignorados,
        errores,
        archivo: file.name,
      });
    } catch (err) {
      setError('No se pudo leer el archivo: ' + (err as Error).message);
    } finally {
      setParsing(false);
    }
  };

  const handleAplicar = () => {
    if (!resumen) return;
    onApply(resumen.updates);
    handleClose();
  };

  const handlePlantilla = () => {
    const contenido = 'cuil,sueldo\n20-12345678-9,150000\n27-26189054-8,200000\n';
    triggerBlobDownload(
      new Blob([contenido], { type: 'text/csv;charset=utf-8' }),
      'plantilla-sueldos-ddjj.csv',
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Importar sueldos</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ fontSize: 13 }}>
            Subí un archivo <strong>CSV</strong> o <strong>Excel (.xlsx)</strong> con las columnas{' '}
            <strong>cuil</strong> y <strong>sueldo</strong>. Se completan los sueldos de los
            empleados cuyo CUIL coincida con la nómina; los CUIL que no estén en la nómina se
            ignoran y se informan. La DDJJ queda en borrador para que la revises antes de confirmar.
          </Alert>

          <Box>
            <Link component="button" type="button" onClick={handlePlantilla} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <DownloadIcon fontSize="small" /> Descargar plantilla CSV
            </Link>
          </Box>

          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={parsing}>
            {parsing ? 'Leyendo…' : 'Elegir archivo (CSV o XLSX)'}
            <input hidden type="file" accept={ACCEPT} onChange={handleFile} />
          </Button>

          {parsing && <CircularProgress size={20} />}
          {error && <Alert severity="error">{error}</Alert>}

          {resumen && (
            <>
              <Divider />
              <Typography variant="subtitle2">Resumen — {resumen.archivo}</Typography>
              <Alert severity={resumen.actualizados > 0 ? 'success' : 'warning'}>
                <strong>{resumen.actualizados}</strong> sueldo(s) a actualizar por CUIL coincidente.
              </Alert>

              {resumen.ignorados.length > 0 && (
                <Alert severity="warning" sx={{ fontSize: 13 }}>
                  <strong>{resumen.ignorados.length}</strong> CUIL del archivo no están en la nómina
                  (se ignoran): {resumen.ignorados.slice(0, 15).join(', ')}
                  {resumen.ignorados.length > 15 ? '…' : ''}
                </Alert>
              )}

              {resumen.errores.length > 0 && (
                <Alert severity="error" sx={{ fontSize: 13 }}>
                  <strong>{resumen.errores.length}</strong> fila(s) con error:
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {resumen.errores.slice(0, 10).map((e, i) => (
                      <li key={i}>Fila {e.fila}: {e.motivo}</li>
                    ))}
                    {resumen.errores.length > 10 && <li>…</li>}
                  </Box>
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleAplicar}
          disabled={!resumen || resumen.actualizados === 0}
        >
          Aplicar {resumen ? resumen.actualizados : ''} sueldo(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
